import { coerceNumberProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, effect, input, OnDestroy, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { optionContainsTerm } from '@src/suite/common/utils/util';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
    selector: 'st-check-list',
    templateUrl: './check-list.html',
    styleUrls: ['./check-list.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-check-list',
    },
})
export class CheckListComponent extends EnpsControlComponent<MultiValue<string> | Array<any>> implements OnDestroy {
    checkListFilter = new FormControl('');
    filteredOptions: Observable<{ id: string; title: string; selectable: boolean }[]>;
    internalValue = signal<string[]>([]);

    selectionList = viewChild(MatSelectionList);

    maxItemsToShow = input(1, {
        transform: (value: number) => {
            return coerceNumberProperty(value, 1);
        },
    });

    itemList = input<{ id: string; title: string; selectable: boolean }[]>([]);
    labelTranslationPrefix = input<string>('');

    selectionChange = output<any>();

    items = computed(() => {
        if (this.asCustomField() && this.itemsToShow()?.length > 0) {
            return this.itemsToShow();
        }
        return this.internalValue();
    });

    itemsToShow = computed(() => {
        return this.internalValue()?.slice(0, this.maxItemsToShow());
    });
    itemsAsTooltip = computed(() => {
        return this.internalValue()
            ?.slice(this.maxItemsToShow())
            .map(item => this.getItemLabel(item))
            .join('\n');
    });

    shouldShowMoreTooltip = computed(() => {
        return this.asCustomField() && this.internalValue()?.length > this.maxItemsToShow();
    });

    showMoreTooltip = computed(() => {
        return `+ ${this.internalValue()?.length - this.maxItemsToShow()} more`;
    });

    searchLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebSearchLabel))();
    });

    constructor() {
        super();
        effect(
            () => {
                const val = [...(this.value() || [])];
                this.internalValue.set(val || []);
            },
            { allowSignalWrites: true }
        );
    }

    onMenuOpened() {
        super.onMenuOpened();
        this.filteredOptions = this.checkListFilter.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || ''))
        );
    }

    private _filter(value: string): { id: string; title: string; selectable: boolean }[] {
        return this.itemList().filter(option => optionContainsTerm(option.title, value) || optionContainsTerm(option.id, value));
    }

    getItemLabel(item: any) {
        return this.itemList().find(option => option.id?.toLowerCase() === item?.toLowerCase())?.title;
    }

    onSelectionChange(event: MatSelectionListChange) {
        let value = 0;
        if (event.options.length > 0) {
            value = event.options[0].value;
        }
        const selectedValue = this.internalValue().find(option => option === event.options[0].value);
        if (selectedValue) {
            this.internalValue.update(values => {
                values?.splice(values?.indexOf(selectedValue), 1);
                return [...(values || [])];
            });
        } else {
            this.internalValue.update(values => {
                return [...(values || []), event.options[0].value];
            });
        }
        this.isValueChanged.set(true);
        this.selectionChange.emit({ id: value, state: [...this.internalValue()] });
    }

    onValueChanged() {
        this.checkListFilter.setValue('');

        if (this.isValueChanged()) {
            this.changed.emit(this.internalValue());
            this.isValueChanged.set(false);
        }
    }

    onRemove(item: string): void {
        const index = this.internalValue().indexOf(item);

        if (index >= 0) {
            this.internalValue.update(values => {
                values?.splice(index, 1);
                return [...(values || [])];
            });
        }
        this.selectionChange.emit({ id: this.internalValue(), state: this.internalValue() });

        setTimeout(() => {
            this.changed.emit(this.internalValue());
        }, 0);
    }

    onUpArrowPress() {
        this.selectionList()?.options?.last?.focus();
    }

    onDownArrowPress() {
        this.selectionList()?.options?.first?.focus();
    }
}
